import { useTranslation } from "react-i18next";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import { default as MUISelect, SelectChangeEvent } from "@mui/material/Select";

import { TestProps } from "shared/types";
import { toCamelCase } from "shared/utils";

import { NONE_SELECTED_OPTION } from "./constants";
import { SelectOption } from "./types";

interface SelectProps<T extends SelectOption> extends TestProps {
  fullWidth?: boolean;
  id?: string;
  options: T[];
  selected?: T;
  allowNoneSelected?: boolean;
  onSelect: (value: T) => void;
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  error?: boolean;
  helperText?: string;
}

const Select = <T extends SelectOption>({
  fullWidth = true,
  id,
  options,
  selected,
  allowNoneSelected,
  onSelect,
  label,
  ariaLabel,
  disabled,
  size,
  testId,
  error,
  helperText,
}: SelectProps<T>) => {
  const { t } = useTranslation("common");

  const noneOption = { ...NONE_SELECTED_OPTION, value: t("none") } as T;

  const modifiedOptions = [
    allowNoneSelected ? noneOption : undefined,
    ...options,
  ].filter(Boolean) as T[];

  const selectedModified =
    allowNoneSelected && !selected ? noneOption : selected;

  const handleOnChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    const option = modifiedOptions.find(({ id }) => id === value);
    if (option) {
      onSelect(option);
    }
  };

  const inputId = id ?? toCamelCase(label || "select-label");

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled} error={error}>
      {label && <FormLabel htmlFor={inputId}>{label}</FormLabel>}
      <MUISelect
        hiddenLabel
        value={(selectedModified?.id as string) || ""}
        onChange={handleOnChange}
        fullWidth={fullWidth}
        disabled={disabled}
        size={size}
        data-testid={testId}
        margin="none"
        className="relative"
        inputProps={{
          id: inputId,
          ...(ariaLabel && { "aria-label": ariaLabel }),
        }}
        MenuProps={{
          disablePortal: false,
        }}
      >
        {modifiedOptions.map((option) => [
          option.divider && (
            <Divider key={`divider-${option.id}`} component="li" />
          ),
          <MenuItem
            key={option.id}
            value={option.id}
            disabled={option.disabled}
            data-testid={option.testId}
          >
            {option.value}
          </MenuItem>,
        ])}
      </MUISelect>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default Select;
